import { useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PhoneFrame, { SCREEN_HEIGHT, SCREEN_WIDTH } from "./PhoneFrame";
import { eyebrow, focusRing, h2Xl, leadText } from "./styles";
import healthShot from "@/assets/screens/health-check.webp";
import analysisShot from "@/assets/screens/home-ai.webp";
import askAiShot from "@/assets/screens/ask-ai.webp";
import dividendShot from "@/assets/screens/dividend-calendar.webp";
import heatmapShot from "@/assets/screens/heatmap.webp";
import holdingShot from "@/assets/screens/holding.webp";

const TABS = [
  { key: "health", img: healthShot, dot: "bg-primary" },
  { key: "analysis", img: analysisShot, dot: "bg-pf-blue" },
  { key: "askAi", img: askAiShot, dot: "bg-pf-teal" },
  { key: "dividends", img: dividendShot, dot: "bg-pf-amber" },
  { key: "heatmap", img: heatmapShot, dot: "bg-pf-heat" },
  { key: "holding", img: holdingShot, dot: "bg-pf-blue" },
] as const;

const ALSO = ["portfolios", "watchlists", "healthCard", "forecasting", "livePrices"] as const;

/**
 * Interactive tour. Auto-advance is driven by the 5s progress hairline's
 * animationend, so pausing (hover / focus) pauses both together. Auto-advance
 * stops after the first user choice and never runs with reduced motion.
 */
const ProductTour = () => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const running = auto && !reduceMotion;
  const paused = hovered || focused;
  const count = TABS.length;

  const choose = (index: number, moveFocus = false) => {
    setActive(index);
    setAuto(false);
    if (moveFocus) tabRefs.current[index]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const rtl = i18n.dir() === "rtl";
    const next = (active + 1) % count;
    const prev = (active - 1 + count) % count;
    const map: Record<string, number> = {
      ArrowDown: next,
      ArrowUp: prev,
      ArrowRight: rtl ? prev : next,
      ArrowLeft: rtl ? next : prev,
      Home: 0,
      End: count - 1,
    };
    if (e.key in map) {
      e.preventDefault();
      choose(map[e.key], true);
    }
  };

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  const current = TABS[active];

  return (
    <section
      id="features"
      aria-labelledby="tour-h"
      className="scroll-mt-[72px] bg-white px-[22px] py-[clamp(88px,9vw,128px)]"
    >
      <div className="mx-auto max-w-[1200px]">
        <div className="pf-rv mx-auto max-w-[820px] text-center">
          <p className={eyebrow}>{t("home.tour.eyebrow")}</p>
          <h2 id="tour-h" className={h2Xl}>
            {t("home.tour.title1")}
            <br />
            {t("home.tour.title2")}
          </h2>
          <p className={`mx-auto mt-[22px] max-w-[600px] ${leadText}`}>{t("home.tour.subtitle")}</p>
        </div>

        <div
          className="mt-12 grid grid-cols-1 items-center gap-7 lg:mt-[72px] lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:gap-12 min-[1100px]:gap-20"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={onBlur}
        >
          <div
            role="tablist"
            aria-label={t("home.tour.tabsLabel")}
            aria-orientation="vertical"
            className="grid grid-cols-2 gap-2 lg:flex lg:flex-col lg:gap-0"
          >
            {TABS.map((tab, i) => {
              const on = i === active;
              return (
                <button
                  key={tab.key}
                  ref={(el) => (tabRefs.current[i] = el)}
                  type="button"
                  role="tab"
                  id={`tour-tab-${tab.key}`}
                  aria-selected={on}
                  aria-controls="tour-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => choose(i)}
                  onKeyDown={onKeyDown}
                  className={`relative block min-h-[48px] w-full overflow-hidden rounded-[14px] border px-3.5 py-3 text-start transition-colors hover:text-foreground lg:rounded-none lg:border-x-0 lg:border-b-0 lg:border-t lg:border-foreground/10 lg:bg-transparent lg:px-0 lg:py-[22px] lg:last:border-b ${
                    on ? "border-primary bg-white text-foreground" : "border-foreground/[0.12] text-pf-ink2"
                  } ${focusRing}`}
                >
                  {on && (
                    <span
                      key={`bar-${active}-${running ? "run" : "still"}`}
                      aria-hidden="true"
                      className={`absolute inset-x-0 top-0 h-0.5 origin-left bg-primary rtl:origin-right lg:-top-px ${
                        running ? "pf-prog" : ""
                      }`}
                      style={running ? { animationPlayState: paused ? "paused" : "running" } : undefined}
                      onAnimationEnd={running ? () => setActive((a) => (a + 1) % count) : undefined}
                    />
                  )}
                  <span className="flex items-center gap-3.5">
                    <span className="hidden w-[22px] text-[13px] font-semibold tabular-nums text-pf-ink2 lg:inline">
                      {`0${i + 1}`}
                    </span>
                    <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${tab.dot}`} />
                    <span className="text-base font-semibold leading-[1.2] tracking-[-0.02em] lg:text-2xl">
                      {t(`home.tour.tabs.${tab.key}.title`)}
                    </span>
                  </span>
                  <span
                    aria-hidden={!on}
                    className={`pf-tab-body hidden max-w-[520px] overflow-hidden ps-[58px] text-[17px] leading-normal text-pf-ink2 lg:block ${
                      on ? "mt-2 max-h-[120px] opacity-100" : "mt-0 max-h-0 opacity-0"
                    }`}
                  >
                    {t(`home.tour.tabs.${tab.key}.body`)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 aspect-square w-[520px] max-w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background:
                  "radial-gradient(closest-side, hsl(var(--pf-sage) / 0.55), hsl(var(--pf-sage-soft) / 0.3) 60%, rgb(255 255 255 / 0) 100%)",
              }}
            />
            <div
              role="tabpanel"
              id="tour-panel"
              aria-labelledby={`tour-tab-${current.key}`}
              className="relative"
            >
              <div className="mx-auto w-[260px] lg:w-[320px]">
                <PhoneFrame size="lg">
                  {TABS.map((tab, i) => (
                    <img
                      key={tab.key}
                      src={tab.img}
                      alt={i === active ? t(`home.tour.tabs.${tab.key}.alt`) : ""}
                      aria-hidden={i === active ? undefined : true}
                      width={SCREEN_WIDTH}
                      height={SCREEN_HEIGHT}
                      loading="lazy"
                      decoding="async"
                      className={`pf-scr ${i === active ? "scale-100 opacity-100" : "scale-[1.035] opacity-0"}`}
                    />
                  ))}
                </PhoneFrame>
              </div>
              <p className="mx-auto mt-[22px] max-w-[420px] text-center text-[17px] leading-normal text-pf-ink2 lg:hidden">
                <b className="font-semibold text-foreground">{t(`home.tour.tabs.${current.key}.title`)}.</b>{" "}
                {t(`home.tour.tabs.${current.key}.body`)}
              </p>
            </div>
            <p className="relative mt-5 text-center text-[13px] text-pf-ink2">{t("home.tour.screensNote")}</p>
          </div>
        </div>

        <div className="pf-rv2 mx-auto mt-[88px] max-w-[1000px] text-center">
          <p className="mb-3.5 text-xs font-semibold uppercase tracking-[0.08em] text-pf-ink2">
            {t("home.tour.alsoTitle")}
          </p>
          <ul className="m-0 flex list-none flex-wrap justify-center p-0">
            {ALSO.map((key, i) => (
              <li
                key={key}
                className={`px-2.5 py-1 text-[15px] text-foreground md:px-[22px] ${
                  i > 0 ? "md:border-s md:border-foreground/15" : ""
                }`}
              >
                {t(`home.tour.also.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProductTour;
